import * as Yup from 'yup';
import t from 'react-translate';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import {
  MentorshipProgramSession,
  PulseCheckResponse,
} from 'redux/schemas/models/mentoring-program-sessions';
import { gray1, gray3, gray6, white } from 'styles/global_defaults/colors';
import {
  largeSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import { Button } from 'react-bootstrap';
import { useAppDispatch } from 'redux/store';
import {
  resetMentoringProgramNextSteps,
  saveMentoringProgramSessionPulseCheck,
} from 'redux/actions/mentoring-program-sessions';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { config } from '@config/pendo.config.json';
import SessionDetails from './session-details';
import PulseCheckQuestions from './pulse-check-questions';
import { getConnectionName } from '../utils';

type PulseCheckFormProps = {
  programId: number;
  enrollmentId: number;
  session: MentorshipProgramSession;
  onSubmit: () => void;
  onClose: () => void;
};

const styles = css`
  padding: 0 ${largeSpacing}px;

  .pulse-check-content {
    height: min(654px, calc(100vh - 210px));
    overflow-y: auto;
  }
  .pulse-check-description {
    padding-top: ${largeSpacing}px;
    color: ${gray1};
  }
  .pulse-check-footer {
    gap: ${threeQuartersSpacing}px;
    height: 116px;
    position: sticky;
    bottom: 0;
    background-color: ${white};
    border-top: 1px solid ${gray6};
    button[disabled] {
      background-color: ${gray6};
      border-color: ${gray6};
      color: ${gray3};
    }
  }
`;

type PulseCheckResponseData = PulseCheckResponse & { questionIndex: number };

const validationSchema = Yup.object().shape({
  pulseCheckResponses: Yup.array()
    .of(
      Yup.object().shape({
        questionIndex: Yup.number().required(),
        questionId: Yup.number().required(),
        response: Yup.mixed().required(),
      }),
    )
    .test(
      'must-respond-scale-questions',
      '',
      (responses: PulseCheckResponseData[]) => {
        if (!responses) return false;
        const indices = responses.map((r) => r.questionIndex);
        return [0, 1, 2].every((i) => indices.includes(i));
      },
    ),
});

const PulseCheckForm = ({
  programId,
  enrollmentId,
  session,
  onSubmit,
  onClose,
}: PulseCheckFormProps) => {
  const dispatch = useAppDispatch();
  const {
    id: sessionId,
    title,
    scheduledDate,
    mentorshipProgramConnection,
    pulseCheckQuestions,
  } = session || {};
  const connectionName = getConnectionName(
    mentorshipProgramConnection,
    enrollmentId,
  );
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });
  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = methods;
  const translationBase = t.MENTORING_PROGRAMS.PARTICIPANT_HOME.SESSION_PULSE_CHECK;
  const dataQATag = config.pendo.athena.mentorshipProgram.participantHome.sessionPulseCheck;

  const handleCancel = () => {
    if (isDirty) {
      dispatch(
        openConfirmationDialog({
          title: translationBase.CANCEL.TITLE(),
          bodyText: translationBase.CANCEL.BODY(),
          confirmText: t.FORM.DISCARD(),
          onConfirm: onClose,
        }),
      );
    } else {
      onClose();
    }
  };

  const onSubmitHandler = ({
    pulseCheckResponses,
  }: {
    pulseCheckResponses: PulseCheckResponseData[];
  }) => {
    dispatch(
      saveMentoringProgramSessionPulseCheck({
        programId,
        sessionId,
        pulseCheckResponses,
      }),
    ).then(() => {
      dispatch(resetMentoringProgramNextSteps({ resetNextSteps: true }));
      onSubmit();
    });
  };

  return (
    <FormProvider {...methods}>
      <form css={styles} onSubmit={handleSubmit(onSubmitHandler)}>
        <div className='pulse-check-content position-relative'>
          <div className='pulse-check-description text-large-body mb-4'>
            {translationBase.FORM.DESCRIPTION(connectionName)}
          </div>
          <SessionDetails title={title} scheduledDate={scheduledDate as string} />
          <PulseCheckQuestions questions={pulseCheckQuestions} />
        </div>
        <div className='d-flex align-items-center justify-content-center pulse-check-footer'>
          <Button
            size='lg'
            variant='secondary'
            onClick={handleCancel}
            data-qa={dataQATag.cancel}
            data-qa-id={`${dataQATag.cancel}_${sessionId}`}
          >
            {t.FORM.CANCEL()}
          </Button>
          <Button
            size='lg'
            type='submit'
            disabled={!isDirty || !isValid}
            data-qa={dataQATag.submit}
            data-qa-id={`${dataQATag.submit}_${sessionId}`}
          >
            {t.FORM.SUBMIT()}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};

export default PulseCheckForm;
